/** 网页预览目录：与后端 DocumentWebTocSupport 使用同一套锚点规则。 */

export function slugifyHeading(title: string): string {
  const s = String(title || '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\u3400-\u9fff._-]/gu, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
  return s || 'section';
}

export function uniqueHeadingId(title: string, used: Set<string>): string {
  const base = slugifyHeading(title);
  let id = base;
  let i = 2;
  while (used.has(id)) {
    id = `${base}-${i}`;
    i += 1;
  }
  used.add(id);
  return id;
}

const WORD_TOC_NOISE = /(?:PAGEREF|HYPERLINK\s+\\l|\bTOC\s+\\)/i;

function isTocHeading(line: string): boolean {
  return /^#{1,3}\s*(目录|目次)\s*$/.test(line.trim())
    || /^(目录|目次)$/.test(line.replace(/\s+/g, ''));
}

function collectMarkdownHeadings(md: string): Array<{ level: number; title: string }> {
  const out: Array<{ level: number; title: string }> = [];
  for (const raw of md.split(/\n/)) {
    const m = /^(#{1,6})\s+(.+?)\s*$/.exec(raw);
    if (!m) continue;
    const title = m[2].trim();
    if (!title || isTocHeading(m[0])) continue;
    out.push({ level: m[1].length, title });
  }
  return out;
}

function buildTocMarkdown(headings: Array<{ level: number; title: string }>): string {
  const used = new Set<string>();
  const counters = [0, 0, 0, 0, 0, 0, 0];
  const lines = ['## 目录', ''];
  for (const h of headings) {
    const level = Math.min(Math.max(h.level, 1), 6);
    counters[level] += 1;
    for (let i = level + 1; i < counters.length; i += 1) counters[i] = 0;
    const num = counters.slice(1, level + 1).map((n) => Math.max(n, 1)).join('.');
    const id = uniqueHeadingId(h.title, used);
    const indent = '  '.repeat(level - 1);
    lines.push(`${indent}- [${num} ${h.title}](#${id})`);
  }
  return lines.join('\n');
}

/** 去掉 Word 域代码目录，改写为 Markdown 超链接目录。 */
export function beautifyDocumentTocMarkdown(markdown: string): string {
  const src = String(markdown || '').replace(/\r\n/g, '\n');
  if (!src.trim()) return src;
  const headings = collectMarkdownHeadings(src);
  if (headings.length < 2) return src;

  const lines = src.split('\n');
  let tocStart = lines.findIndex((l) => isTocHeading(l) || WORD_TOC_NOISE.test(l));
  if (tocStart < 0) return src;

  let tocEnd = tocStart + 1;
  while (tocEnd < lines.length) {
    const line = lines[tocEnd];
    const heading = /^(#{1,6})\s+/.exec(line);
    if (heading && !isTocHeading(line)) break;
    if (WORD_TOC_NOISE.test(line) || isTocHeading(line) || /^\s*[-*]\s+\[.+\]\(#.+\)$/.test(line)
      || /^\s*$/.test(line) || /^\d+(\.\d+)*\S.{0,40}\d{1,3}$/.test(line.trim())) {
      tocEnd += 1;
      continue;
    }
    break;
  }

  const rebuilt = [...lines.slice(0, tocStart), buildTocMarkdown(headings), '', ...lines.slice(tocEnd)]
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return rebuilt;
}
